/**
 * Data layer for `student-startup-tier`. Thin typed fetch wrappers over /api/v1/student-startup-tier.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface StudentStartupTierRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListStudentStartupTierResponse {
	count: number;
	items: StudentStartupTierRow[];
}

export interface CreateStudentStartupTierBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/student-startup-tier";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListStudentStartupTierResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListStudentStartupTierResponse>(res);
}

export async function create(body: CreateStudentStartupTierBody): Promise<StudentStartupTierRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<StudentStartupTierRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const studentStartupTierQueryKeys = {
	all: ["student-startup-tier"] as const,
	list: () => [...studentStartupTierQueryKeys.all] as const,
	detail: (id: string) => [...studentStartupTierQueryKeys.all, id] as const,
};
